import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { DOCUMENT_TYPES, getDocumentTypeConfig } from '../lib/documentTypes'

// NOTE: was a local TYPE_CONFIG duplicate here — now reads from the single shared source
// (lib/documentTypes.js), same one DocumentCenter.jsx and DocumentPanel.jsx use.

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'

const fmtAge = (d) => {
  if (!d) return '—'
  const days = Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
  if (days < 1) return 'Today'
  if (days === 1) return '1 day'
  if (days < 30) return `${days} days`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} mo`
  return `${Math.floor(months / 12)} yr`
}

// Roles allowed to view this workspace — front-end check only, pending the
// real permissions system (pre-launch checklist item #2).
// Matches the actual role values used across the app (see AuthContext.jsx `can` object).
const ALLOWED_ROLES = ['admin', 'director_operations', 'director_commercial', 'cost_control']

export default function DocumentGovernance({ initProject }) {
  const { role } = useAuth()
  const [selectedProject, setSelectedProject] = useState(null)
  const [projects, setProjects]               = useState([])
  const [rows, setRows]                       = useState([])
  const [loading, setLoading]                 = useState(false)
  const [typeFilter, setTypeFilter]           = useState('all')
  const [missingOnly, setMissingOnly]         = useState(true)

  useEffect(() => {
    supabase.from('projects').select('id, name').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (initProject) setSelectedProject(initProject)
  }, [initProject])

  useEffect(() => {
    if (selectedProject?.id) loadCoverage(selectedProject.id)
  }, [selectedProject?.id]) // eslint-disable-line

  const loadCoverage = async (projectId) => {
    setLoading(true)
    const { data, error } = await supabase
      .from('v_document_coverage')
      .select('*')
      .eq('project_id', projectId)
      .order('entity_created_at', { ascending: false })
    if (error) console.error('coverage error:', error.message)
    setRows(data || [])
    setLoading(false)
  }

  // Access gate — front-end only, per Stage 2 scope
  const hasAccess = ALLOWED_ROLES.includes(role)

  // Summary per type — pure aggregation of already-fetched rows, no recomputation of business rules
  const summary = useMemo(() => {
    const byType = {}
    for (const r of rows) {
      const t = r.entity_type
      if (!byType[t]) byType[t] = { total: 0, withDoc: 0, isRequired: r.is_required }
      byType[t].total++
      if (r.has_document) byType[t].withDoc++
    }
    return byType
  }, [rows])

  const filtered = rows.filter(r => {
    if (typeFilter !== 'all' && r.entity_type !== typeFilter) return false
    if (missingOnly && !r.is_missing) return false
    return true
  })

  if (!hasAccess) {
    return (
      <div style={{ padding:'40px 24px', textAlign:'center', color:'#6B7280' }}>
        <div style={{ fontSize:14, fontWeight:600 }}>Access restricted</div>
        <div style={{ fontSize:12, marginTop:4 }}>This workspace is limited to governance and audit roles.</div>
      </div>
    )
  }

  if (!selectedProject) return (
    <div style={{ padding:'32px 24px', maxWidth:700 }}>
      <h1 style={{ fontSize:22, fontWeight:800, color:'#0F1017', marginBottom:6 }}>Document Governance</h1>
      <p style={{ fontSize:13, color:'#6B7280', marginBottom:24 }}>متابعة اكتمال المستندات الإجبارية عبر المشروع</p>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
        {projects.map(p => (
          <button key={p.id} onClick={() => setSelectedProject(p)}
            style={{ textAlign:'left', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:16, cursor:'pointer' }}>
            <div style={{ fontSize:13, fontWeight:600, color:'#0F1017' }}>{p.name}</div>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', fontFamily:"-apple-system,'Helvetica Neue',sans-serif" }}>

      {/* Header */}
      <div style={{ padding:'18px 24px 14px', background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
        <div style={{ fontSize:20, fontWeight:800, color:'#0F1017' }}>Document Governance</div>
        <div style={{ fontSize:11.5, color:'#6B7280', marginTop:3 }}>
          {selectedProject.name}
          <button onClick={() => setSelectedProject(null)} style={{ marginLeft:8, fontSize:10, color:'#9CA3AF', border:'1px solid #E5E7EB', borderRadius:4, padding:'1px 6px', cursor:'pointer', background:'none' }}>× Change</button>
        </div>

        {/* Summary strip */}
        <div style={{ display:'flex', gap:8, marginTop:14, flexWrap:'wrap' }}>
          {Object.entries(DOCUMENT_TYPES).map(([type, cfg]) => {
            const s = summary[type]
            const pct = s && s.total > 0 ? Math.round((s.withDoc / s.total) * 100) : null
            const isOptional = s && !s.isRequired
            return (
              <button key={type} onClick={() => setTypeFilter(typeFilter === type ? 'all' : type)}
                style={{
                  padding:'8px 14px', borderRadius:10, cursor:'pointer', textAlign:'left', minWidth:120,
                  border: typeFilter===type ? `1.5px solid ${cfg.color}` : '1px solid #E5E7EB',
                  background: typeFilter===type ? cfg.bg : '#fff',
                }}>
                <div style={{ fontSize:10.5, fontWeight:600, color:'#6B7280' }}>{cfg.label}</div>
                <div style={{ fontSize:16, fontWeight:800, color: isOptional ? '#9CA3AF' : (pct === 100 ? '#059669' : pct === null ? '#9CA3AF' : cfg.color) }}>
                  {!s ? '—' : isOptional ? 'Optional' : `${pct}%`}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 24px', background:'#fff', borderBottom:'1px solid #E5E7EB', flexShrink:0 }}>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          style={{ padding:'5px 10px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:12, background:'#fff', cursor:'pointer' }}>
          <option value="all">All Types</option>
          {Object.entries(DOCUMENT_TYPES).map(([type, cfg]) => (
            <option key={type} value={type}>{cfg.label}</option>
          ))}
        </select>

        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'#374151', cursor:'pointer' }}>
          <input type="checkbox" checked={missingOnly} onChange={e => setMissingOnly(e.target.checked)} />
          Show Missing Only
        </label>

        <div style={{ flex:1 }} />
        <div style={{ fontSize:11, color:'#9CA3AF' }}>{filtered.length} record{filtered.length === 1 ? '' : 's'}</div>
      </div>

      {/* Compliance list */}
      <div style={{ flex:1, overflow:'auto', padding:'16px 24px' }}>
        {loading ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, color:'#9CA3AF' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:'60px 0', color:'#059669' }}>
            <div style={{ fontSize:28, marginBottom:8 }}>✓</div>
            <div style={{ fontSize:14, fontWeight:600 }}>All mandatory documents are attached.</div>
          </div>
        ) : (
          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead>
                <tr style={{ background:'#F9FAFB' }}>
                  {['Entity','Reference','Created','Age','Requirement','Compliance'].map(h => (
                    <th key={h} style={{ padding:'9px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6B7280', borderBottom:'1px solid #E5E7EB' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const cfg = getDocumentTypeConfig(r.entity_type)
                  return (
                    <tr key={r.entity_id} style={{ background: i%2===0?'#fff':'#FAFAFA', borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{ padding:'10px 14px' }}>
                        <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:12, background:cfg.bg, color:cfg.color }}>
                          {cfg.label}
                        </span>
                      </td>
                      <td style={{ padding:'10px 14px', fontWeight:600, color:'#0F1017' }}>{r.entity_ref || '—'}</td>
                      <td style={{ padding:'10px 14px', color:'#6B7280' }}>{fmtDate(r.entity_created_at)}</td>
                      <td style={{ padding:'10px 14px', color:'#6B7280' }}>{fmtAge(r.entity_created_at)}</td>
                      <td style={{ padding:'10px 14px', color:'#6B7280' }}>{r.requirement_label}</td>
                      <td style={{ padding:'10px 14px' }}>
                        {r.is_missing ? (
                          <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:12, background:'#FEE2E2', color:'#DC2626' }}>Missing</span>
                        ) : r.has_document ? (
                          <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:12, background:'#DCFCE7', color:'#059669' }}>Attached</span>
                        ) : (
                          <span style={{ fontSize:11, fontWeight:600, padding:'2px 8px', borderRadius:12, background:'#F3F4F6', color:'#9CA3AF' }}>—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
